import { useState } from "react";
import { mockProducts } from "@/mocks/products";

interface ShareProductModalProps {
  onClose: () => void;
  onShare: (post: { content: string; productId: string; productTitle: string; productImage: string; referralLink: string }) => void;
}

const platforms = [
  { key: "tiktok", label: "TikTok", icon: "ri-tiktok-line", color: "bg-gray-900 text-white" },
  { key: "youtube", label: "YouTube", icon: "ri-youtube-line", color: "bg-red-500 text-white" },
  { key: "facebook", label: "Facebook", icon: "ri-facebook-fill", color: "bg-blue-600 text-white" },
  { key: "instagram", label: "Instagram", icon: "ri-instagram-line", color: "bg-rose-500 text-white" },
  { key: "twitter", label: "Twitter/X", icon: "ri-twitter-x-line", color: "bg-gray-800 text-white" },
  { key: "douyin", label: "Douyin", icon: "ri-tiktok-line", color: "bg-black text-white" },
  { key: "weixin", label: "WeChat", icon: "ri-wechat-line", color: "bg-green-500 text-white" },
];

export default function ShareProductModal({ onClose, onShare }: ShareProductModalProps) {
  const [selectedProduct, setSelectedProduct] = useState(mockProducts[0]);
  const [caption, setCaption] = useState("");
  const [copiedPlatform, setCopiedPlatform] = useState<string | null>(null);
  const [sharedToFeed, setSharedToFeed] = useState(false);

  const referralLink = `${window.location.origin}/product/${selectedProduct.id}?ref=myhandle`;

  const handleCopy = (platform: string) => {
    navigator.clipboard.writeText(referralLink);
    setCopiedPlatform(platform);
    setTimeout(() => setCopiedPlatform(null), 2000);
  };

  const handleShareToFeed = () => {
    const postCaption = caption || `Check out this amazing product from a verified factory! 🔥 ${selectedProduct.title} — only $${selectedProduct.price}`;
    onShare({
      content: postCaption,
      productId: selectedProduct.id,
      productTitle: selectedProduct.title,
      productImage: selectedProduct.image,
      referralLink,
    });
    setSharedToFeed(true);
    setTimeout(() => { setSharedToFeed(false); onClose(); }, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
            <i className="ri-share-line text-orange-500" /> Share Product &amp; Earn Commission
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer text-gray-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Product Selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Product to Promote</label>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {mockProducts.filter((p) => p.verified).map((product) => (
                <button
                  key={product.id}
                  onClick={() => setSelectedProduct(product)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer text-left ${selectedProduct.id === product.id ? "border-orange-400 bg-orange-50" : "border-gray-100 hover:border-gray-200"}`}
                >
                  <img src={product.image} alt={product.title} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 line-clamp-1">{product.title}</p>
                    <p className="text-xs text-gray-500">{product.seller} · ${product.price}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400">Commission</p>
                    <p className="text-sm font-extrabold text-rose-600">
                      {product.id === "1" ? "12%" : product.id === "4" ? "15%" : product.id === "8" ? "8%" : "10%"}
                    </p>
                  </div>
                  {selectedProduct.id === product.id && (
                    <i className="ri-checkbox-circle-fill text-orange-500 text-lg flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Referral Link */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Your Referral Link</label>
            <div className="flex gap-2">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-xs text-gray-500 font-mono truncate">
                {referralLink}
              </div>
              <button
                onClick={() => handleCopy("link")}
                className={`px-3 py-2.5 rounded-lg text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${copiedPlatform === "link" ? "bg-green-500 text-white" : "bg-orange-500 text-white hover:bg-orange-600"}`}
              >
                {copiedPlatform === "link" ? <span><i className="ri-checkbox-circle-fill mr-1" />Copied!</span> : <span><i className="ri-file-copy-line mr-1" />Copy</span>}
              </button>
            </div>
          </div>

          {/* Share to Platforms */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Share to Your Platform</label>
            <div className="grid grid-cols-4 gap-2">
              {platforms.map((p) => (
                <button
                  key={p.key}
                  onClick={() => handleCopy(p.key)}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl text-xs font-semibold transition-all cursor-pointer ${copiedPlatform === p.key ? "bg-green-500 text-white scale-95" : p.color}`}
                >
                  <i className={`${p.icon} text-lg`} />
                  <span className="text-xs">{copiedPlatform === p.key ? "Copied!" : p.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">Clicking copies your referral link — paste it in your post caption</p>
          </div>

          {/* Caption for Feed Post */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Post Caption (for StarBookPrime Feed)</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder={`Check out this amazing product from a verified factory! 🔥 ${selectedProduct.title} — only $${selectedProduct.price}`}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-orange-400 resize-none transition-colors"
            />
            <div className="text-xs text-gray-400 text-right mt-1">{caption.length}/500</div>
          </div>

          {/* Commission Preview */}
          <div className="bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-100 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">You earn per sale of</p>
                <p className="text-sm font-semibold text-gray-900 line-clamp-1">{selectedProduct.title}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-extrabold text-green-600">
                  +${(selectedProduct.price * (selectedProduct.id === "1" ? 0.12 : selectedProduct.id === "4" ? 0.15 : 0.10)).toFixed(2)}
                </p>
                <p className="text-xs text-gray-400">via PayPal</p>
              </div>
            </div>
          </div>

          {/* Share to Feed Button */}
          <button
            onClick={handleShareToFeed}
            className="w-full py-3.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            {sharedToFeed ? (
              <span><i className="ri-checkbox-circle-fill mr-2" />Posted to Feed!</span>
            ) : (
              <span><i className="ri-send-plane-line mr-2" />Post to StarBookPrime Feed</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
